<template>
  <div class="product__main-img-column-wrapper">
    <div :class="mainImgWrapperClasses">
      <div class="product__main-img-inner-wrapper">
        <div class="product__main-img-inner-wrapper-container">
          <div
            v-if="selectedFrame"
            class="product__main-img-header product__main-img-header--display"
          >
            <div class="product__main-img-header-buttons">
              <div
                v-if="allowRetouching"
                class="product__main-img-button product__main-img-button--retouching"
                @click="handleRetouchingsClick"
              >
                <div
                  :aria-label="contentfulText('enhancements-label').Content"
                  class="product__main-img-button-icon product__main-img-button-icon--enhancements"
                />
              </div>
              <div
                v-if="allowCropping"
                class="product__main-img-button product__main-img-button--crop"
                @click="handleCropClicked"
              >
                <div
                  :aria-label="contentfulText('crop-label').Content"
                  class="product__main-img-button-icon product__main-img-button-icon--crop"
                />
              </div>
            </div>
          </div>
          <img
            v-if="!selectedFrame"
            :src="selectionPlaceHolderImg"
            :class="mainImgClasses"
            :alt="contentfulText('single-frame-package-main-image-select-photo-label').Content"
            :title="contentfulText('single-frame-package-main-image-select-photo-label').Content"
            @click="showSelectPhotoModal = true"
          >
          <img
            v-if="selectedFrame"
            :src="selectedFrame.Urls.SmallWatermarked"
            :class="mainImgClasses"
            :alt="selectedFrameImageAltText"
            :title="selectedFrameImageAltText"
            @click="handleMainImgClick"
          >
          <div
            v-if="selectedFrame"
            :class="footerClasses"
          >
            <div
              v-if="!isPreOrder"
              class="product__main-img-footer-buttons"
            >
              <div
                class="product__main-img-button"
                @click="showSelectPhotoModal = true"
              >
                <contentful-text
                  v-if="isMobile"
                  :item="contentfulText('single-frame-packacge-select-photo-button-label-mobile')"
                />
                <contentful-text
                  v-if="!isMobile"
                  :item="contentfulText('single-frame-packacge-select-photo-button-label-desktop')"
                />
              </div>
            </div>
          </div>
          <div
            v-if="selectedFrame && selectedFrame.IsFavorite"
            class="product__main-img-favorite-icon-wrapper"
          >
            <i class="v2-icon-favorite product__main-img-favorite-icon"/>
          </div>
        </div>
        <div
          v-if="!isPreOrder"
          class="product__zoom"
          @click="showZoomModal = true"
        />
        <div
          v-if="selectedFrame"
          class="product__modifications"
        >
          <div
            v-if="numberRetouchingsApplied > 0"
            :title="enhancementsModificationTitle"
            class="product__modification product__modification--display product__modification--retouching"
          >
            {{ numberRetouchingsApplied }}
          </div>
          <div
            v-if="selectedFrame.IsCropped"
            :title="contentfulText('custom-crop-label').Content"
            class="product__modification product__modification--display product__modification--crop"
          >
            <contentful-text :item="contentfulText('custom-crop-label')"/>
          </div>
        </div>
        <input
          :value="selectedFrameId"
          type="hidden"
          name="SelectedFrameId"
        >
      </div>
    </div>
    <select-photo-modal
      :show="showSelectPhotoModal"
      @close="showSelectPhotoModal = false"
      @photo-selected="handlePhotoSelected"
    />
    <crop-modal
      v-if="selectedFrame"
      :show="showCropModal"
      :frame="selectedFrame"
      :aspect-ratio-name="optionProduct.AspectRatioName"
      :aspect-ratio="optionProduct.AspectRatio"
      :saved-crop="savedCropData"
      @close="showCropModal = false"
    />
    <zoom-modal
      v-if="selectedFrame"
      :show="showZoomModal"
      :frame="selectedFrame"
      @close="showZoomModal = false"
    />
  </div>
</template>

<script>

import CropModal from '@Shared/components/catalog/CropModal/CropModal'
import SelectPhotoModal from '@Shared/components/catalog/SelectPhotoModal/SelectPhotoModal'
import ZoomModal from '@Shared/components/catalog/ZoomModal/ZoomModal'
import ContentfulMixin from '@Shared/mixins/contentful'
import ResponsiveBreakpointsMixin from '@Shared/mixins/responsive-breakpoints'
import ToastMixin from '@Shared/mixins/toast'
import selectionPlaceHolderImg from '@Shared/img/selection-placeholder.png'
import { find } from 'lodash'

export default {
  name: 'SingleFramePackage',
  components: {
    CropModal,
    SelectPhotoModal,
    ZoomModal
  },
  mixins: [
    ContentfulMixin,
    ResponsiveBreakpointsMixin,
    ToastMixin
  ],
  props: {
    allowCropping: { type: Boolean, default: false },
    allowRetouching: { type: Boolean, default: false },
    optionProduct: { type: Object, default: null, required: true }
  },
  data () {
    return {
      savedCropData: null,
      selectedFrameId: null,
      showCropModal: false,
      showSelectPhotoModal: false,
      showZoomModal: false,
      selectionPlaceHolderImg
    }
  },
  computed: {
    selectedFrame () {
      if (this.selectedFrameId === null) {
        return null
      }

      return this.$store.getters.getFrame(this.selectedFrameId)
    },
    footerClasses () {
      return {
        'product__main-img-footer': true,
        'product__main-img-footer--display': true,
        'product__main-img-footer--no-retouchings': !this.allowCropping && !this.allowRetouching
      }
    },
    frameIsRetouched () {
      return false
    },
    frameIsCropped () {
      return false
    },
    mainImgWrapperClasses () {
      return {
        'product__main-img-wrapper': true,
        'can-be-retouchingd': this.allowRetouching, // need to fix the spelling of this system-wide
        'is-blank': this.selectedFrameId === null
      }
    },
    alwaysShowRetouchingModal () {
      return this.$store.state.catalogPageCommon.alwaysShowRetouchingModal
    },
    numberRetouchingsApplied () {
      return this.selectedFrame.RetouchingPackages.length + this.$store.getters.selectedRetouchingsByFrame(this.selectedFrame.FrameID).length
    },
    enhancementsModificationTitle () {
      return this.contentfulTextFormatted('enhancements-count', { numEnhancements: this.numberRetouchingsApplied })
    },
    showSingleFramePackageValidationError () {
      return this.$store.state.showSingleFramePackageValidationError
    },
    content () {
      return this.$store.state.singleFramePackageModuleContent
    },
    isPreOrder () {
      return this.$store.state.isPreOrder
    },
    mainImgClasses () {
      return {
        'product__main-img': true,
        'responsive-img': true,
        'product__main-img--no-pointer': this.isPreOrder === true
      }
    },
    selectedFrameImageAltText () {
      if (this.$store.state.isPreOrder === true) {
        return this.contentfulText('single-frame-package-main-image-selected-photo-label-preorder').Content
      } else {
        return this.contentfulText('single-frame-package-main-image-selected-photo-label').Content
      }
    }
  },
  watch: {
    selectedFrameId (v, ov) {
      this.$store.commit('setSelectedFrameId', v)
    },
    showSingleFramePackageValidationError (v, ov) {
      if (v === true) {
        this.makeToast(
          'error',
          this.contentfulText('single-frame-package-toast-validation-failed-title').Content,
          this.contentfulText('single-frame-package-toast-validation-failed-message').Content
        )
        this.$store.commit('setShowSingleFramePackageValidationError', false)
      }
    }
  },
  mounted () {
    if (this.isPreOrder === true) {
      this.selectedFrameId = -1
    }
  },
  methods: {
    handleMainImgClick () {
      if (this.isPreOrder === true) return // if occasion is pre-order user doesn't need to select another image or view placeholder in zoom UI

      if (this.selectedFrameId === null) {
        this.showSelectPhotoModal = true
      } else {
        this.showZoomModal = true
      }
    },
    handlePhotoSelected (data) {
      this.selectedFrameId = data.frameInfo.FrameID

      if (this.alwaysShowRetouchingModal === true) {
        document.dispatchEvent(new CustomEvent('retouchingModal.show', {
          detail: { frameId: data.frameInfo.FrameID, alwaysShow: true }
        }))
      }
    },
    handleRetouchingsClick () {
      document.dispatchEvent(new CustomEvent('retouchingModal.show', {
        detail: { frameId: this.selectedFrame.FrameID, alwaysShow: true }
      }))
    },
    handleCropClicked () {
      if (this.selectedFrame.IsCropped === true) {
        // get saved crop data
        this.$http.post(`${this.$store.state.catalogPageCommon.siteRoot}Ajax/GetFrameCropData`, { frameId: this.selectedFrame.FrameID }).then(resp => {
          if (resp.status !== 200) {
            this.savedCropData = null
            return
          }

          // get the crop data for the option product's aspect ratio
          let cropMatchingAspectRatio = find(resp.data.CroppingData[0].Value.Crops, crop => {
            return crop.AspectRatioTypeString === this.optionProduct.AspectRatioName && crop.TargetHeight > 0 && crop.TargetWidth > 0
          })

          if (cropMatchingAspectRatio !== undefined && cropMatchingAspectRatio !== false) {
            this.savedCropData = cropMatchingAspectRatio
          } else {
            this.savedCropData = null
          }
        }).catch(e => {
          this.savedCropData = null
        }).finally(() => {
          this.showCropModal = true // show the crop modal
        })
      } else {
        this.showCropModal = true
      }
    },
    handleZoomClick () {
      this.showZoomModal = true
    }
  }
}
</script>

<style lang="scss" scoped>
.product {
  &__main-img {
    &--no-pointer {
      cursor: default;
    }
  }
}
</style>



// WEBPACK FOOTER //
// WebResources/vue/default/catalog-details/components/SingleFramePackage/SingleFramePackage.vue